import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PlayCircle, Sparkles, ShieldCheck, Building2, Landmark, Users, Globe2, Boxes, MessagesSquare, Mail, MessageCircle } from 'lucide-react';
import { PublicLayout, Section } from '@/components/landing/PublicLayout';
import { PricingSection, TestimonialCard, ContactForm } from '@/components/landing/Blocks';
import { Button } from '@/components/ui/button';
import { useLanding, MODULE_SHOTS, waLink } from '@/lib/landing';
import { useLang } from '@/lib/i18n';
import { useCopy } from '@/lib/landingCopy';
import { useSeo } from '@/lib/seo';
import { fmtIDR } from '@/lib/api';

const BrowserFrame = ({ src, alt, className = '' }) => (
  <div className={`overflow-hidden rounded-2xl border border-border bg-white shadow-[0_30px_80px_-20px_rgba(27,42,107,0.35)] ${className}`}>
    <div className="flex items-center gap-1.5 border-b border-border bg-[hsl(var(--pillot-sand))] px-3 py-2"><span className="h-2.5 w-2.5 rounded-full bg-rose-300" /><span className="h-2.5 w-2.5 rounded-full bg-amber-300" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-300" /><span className="ml-3 h-4 flex-1 rounded-md bg-white/80 text-[10px] leading-4 px-2 text-muted-foreground">app.pillot.id</span></div>
    <img src={src} alt={alt} className="block w-full" loading="lazy" />
  </div>
);

const ICONS = [Users, Building2, Boxes, Landmark, ShieldCheck, Globe2, MessagesSquare, Sparkles];

export default function LandingPage() {
  const d = useLanding();
  const { lang } = useLang();
  const c = useCopy(lang);
  useSeo({ title: c.seo_home_title, description: c.seo_home_desc, path: '/', lang });
  const annual = d?.annual_months_charged || 10;
  const [annA, annB] = c.hero_annual(annual);
  return (
    <PublicLayout>
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_left,hsl(var(--pillot-mint-soft))_0%,transparent_55%)]" />
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 pt-16 pb-10 lg:grid-cols-[1fr_1.15fr] lg:items-center lg:pt-24 lg:pb-20">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs font-medium shadow-soft"><span className="h-2 w-2 rounded-full bg-[hsl(var(--pillot-mint))] animate-pulse" />{c.hero_badge}</span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight" data-testid="hero-title">{c.hero_title_a}<span className="text-primary">{c.hero_title_b}</span></h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">{c.hero_desc}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild className="h-12 rounded-full px-7 text-base" data-testid="hero-cta-register"><Link to="/register">{c.hero_cta(d?.trial_days || 14)} <ArrowRight size={18} className="ml-1.5" /></Link></Button>
              <Button asChild variant="outline" className="h-12 rounded-full px-7 text-base bg-white" data-testid="hero-cta-library"><Link to="/library"><PlayCircle size={18} className="mr-1.5" />{c.hero_tutorial}</Link></Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-sm text-muted-foreground">
              <span>{c.hero_from} <strong className="text-foreground">{fmtIDR(d?.price_per_user_idr || 0)}</strong>{c.hero_per}</span>
              <span>{annA}<strong className="text-foreground">{annB}</strong></span>
              <span>{c.hero_nocard}</span>
            </div>
          </div>
          <div className="relative fade-up" style={{ animationDelay: '120ms' }}>
            <BrowserFrame src="/screenshots/accounting.jpg" alt="Pillot Accounting" className="lg:rotate-[-1.5deg]" />
            <BrowserFrame src="/screenshots/hr.jpg" alt="Pillot HR" className="absolute -bottom-8 -left-6 hidden w-[55%] sm:block lg:rotate-[2deg]" />
          </div>
        </div>
      </section>

      <section className="border-y border-border/60 bg-white/60" data-testid="landing-modules">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 grid gap-4 grid-cols-2 md:grid-cols-4">
          {c.modules.map(([name, desc], i) => { const Icon = ICONS[i]; return (
            <div key={name} className="group rounded-2xl border border-border bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-soft fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(27,42,107,0.08)] text-primary transition-colors group-hover:bg-primary group-hover:text-white"><Icon size={18} /></div>
              <div className="mt-4 font-display font-semibold">{name}</div>
              <div className="mt-1 text-xs text-muted-foreground">{desc}</div>
            </div>
          ); })}
        </div>
      </section>

      <Section id="fitur" eyebrow={c.features_eyebrow} title={c.features_title} desc={c.features_desc} testid="landing-features">
        <div className="space-y-24">
          {MODULE_SHOTS.map((f, i) => (
            <div key={f.key} className={`grid gap-10 lg:grid-cols-2 lg:items-center ${i % 2 ? 'lg:[&>*:first-child]:order-2' : ''}`} data-testid={`feature-${f.key}`}>
              <BrowserFrame src={f.img} alt={c.shots[i][0]} />
              <div className="max-w-md lg:px-6">
                <span className="font-mono text-xs text-[hsl(var(--pillot-mint))]">0{i + 1}</span>
                <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">{c.shots[i][0]}</h3>
                <p className="mt-4 text-base text-muted-foreground leading-relaxed">{c.shots[i][1]}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <section className="bg-white border-y border-border/60">
        <Section id="harga" eyebrow={c.pricing_eyebrow} title={c.pricing_title} desc={c.pricing_desc(annual)} testid="landing-pricing">
          <PricingSection data={d} />
        </Section>
      </section>

      {d?.testimonials?.length > 0 && (
        <Section eyebrow={c.testi_eyebrow} title={c.testi_title} testid="landing-testimonials">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{d.testimonials.map((t) => <TestimonialCard key={t.id} t={t} />)}</div>
        </Section>
      )}

      <section className="bg-white border-y border-border/60">
        <Section id="kontak" eyebrow={c.contact_eyebrow} title={c.contact_title} desc={c.contact_desc} testid="landing-contact">
          <div className="grid gap-8 lg:grid-cols-[1fr_1.4fr]">
            <div className="space-y-4 text-sm">
              {d?.contact_email && <a href={`mailto:${d.contact_email}`} className="flex items-center gap-3 rounded-2xl border border-border p-4 hover:border-primary/40 transition-colors" data-testid="contact-card-email"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(27,42,107,0.08)] text-primary"><Mail size={18} /></span><span><span className="block text-xs text-muted-foreground">Email</span><span className="font-medium">{d.contact_email}</span></span></a>}
              {d?.contact_whatsapp && <a href={waLink(d.contact_whatsapp)} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-2xl border border-border p-4 hover:border-primary/40 transition-colors" data-testid="contact-card-wa"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--pillot-mint-soft))] text-[hsl(var(--pillot-mint))]"><MessageCircle size={18} /></span><span><span className="block text-xs text-muted-foreground">WhatsApp</span><span className="font-medium">{d.contact_whatsapp}</span></span></a>}
            </div>
            <ContactForm />
          </div>
        </Section>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-24" data-testid="landing-cta">
        <div className="relative overflow-hidden rounded-3xl bg-primary px-8 py-14 sm:px-14 text-white noise">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[hsl(var(--pillot-mint))]/30 blur-3xl" />
          <div className="relative grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">{c.cta_title}</h2>
              <p className="mt-4 text-white/75 text-base max-w-lg">{c.cta_desc}</p>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
              <Button asChild className="h-12 rounded-full bg-white px-7 text-primary hover:bg-white/90 text-base" data-testid="cta-register"><Link to="/register">{c.cta_register}</Link></Button>
              <Button asChild variant="ghost" className="h-12 rounded-full px-7 text-white hover:bg-white/10 text-base"><Link to="/pricing">{c.cta_pricing}</Link></Button>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
